import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CEFR } from "@/lib/constants";
import LanguageSelect from "@/components/LanguageSelect";
import SerbianScriptSelect from "@/components/SerbianScriptSelect";
import { pairAllowed, isSerbian } from "@/lib/languages";
import { useI18n } from "@/context/I18nContext";
import { safeReturn, rememberReturn } from "@/lib/returnPath";

function GoogleButton() {
  const { t } = useI18n();
  const go = () => {
    // Remember only a validated internal return path (e.g. a class invite) for after Google sign-in.
    rememberReturn(new URLSearchParams(window.location.search).get("next"));
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={go} data-testid="google-auth-btn" className="nb-btn-outline w-full py-3">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
      {t("auth.continueWithGoogle")}
    </button>
  );
}

export default function Auth({ mode = "login" }) {
  const isSignup = mode === "signup";
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    email: "", password: "", name: "", native_lang: "en", target_lang: "es", level: "A1", accept_terms: false, target_script: "cyrillic",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickNative = (v) => setForm((f) => ({ ...f, native_lang: v, target_lang: pairAllowed(v, f.target_lang) ? f.target_lang : "en" }));
  const pickTarget = (v) => setForm((f) => ({ ...f, target_lang: v, native_lang: pairAllowed(f.native_lang, v) ? f.native_lang : "en" }));
  const showScript = isSignup && (isSerbian(form.target_lang) || isSerbian(form.native_lang));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isSignup ? "/auth/signup" : "/auth/login";
      const body = { ...form };
      if (!showScript) delete body.target_script;
      const payload = isSignup ? { ...body, referred_by: localStorage.getItem("fluent_ref") || null } : { email: form.email, password: form.password };
      const res = await api.post(endpoint, payload);
      loginWithToken(res.data.session_token, res.data.user);
      if (isSignup) localStorage.removeItem("fluent_ref");
      toast.success(isSignup ? t("auth.welcomeNew") : t("auth.welcomeBack"));
      const next = safeReturn(new URLSearchParams(window.location.search).get("next"));
      navigate(next || "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <div className="max-w-md w-full mx-auto px-4 py-8 flex-1">
        <Link to="/" className="flex items-center gap-2 font-cabinet font-bold mb-6"><ArrowLeft className="w-5 h-5 rtl:rotate-180" /> <span dir="ltr">GetFluent<span className="text-coral">Loop</span></span></Link>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="nb-card p-6 md:p-8">
          <h1 className="font-cabinet font-extrabold text-3xl">{isSignup ? t("auth.signUpTitle") : t("auth.signInTitle")}</h1>
          <p className="text-gray-600 text-sm mt-1">{isSignup ? t("auth.signUpSub") : t("auth.signInSub")}</p>

          <div className="mt-6"><GoogleButton /></div>
          <div className="flex items-center gap-3 my-5"><div className="flex-1 h-0.5 bg-gray-200" /><span className="text-xs text-gray-400 font-bold">{t("auth.or")}</span><div className="flex-1 h-0.5 bg-gray-200" /></div>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <input className="nb-input" placeholder={t("auth.yourName")} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="auth-name" />
            )}
            <input className="nb-input" type="email" required placeholder={t("auth.email")} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="auth-email" />
            <input className="nb-input" type="password" required minLength={6} placeholder={t("auth.password")} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="auth-password" />

            {isSignup && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest">{t("auth.iSpeak")}</label>
                    <LanguageSelect role="native" value={form.native_lang} onChange={pickNative} partner={form.target_lang} testid="auth-native" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest">{t("auth.imLearning")}</label>
                    <LanguageSelect role="target" value={form.target_lang} onChange={pickTarget} partner={form.native_lang} testid="auth-target" />
                  </div>
                </div>
                {showScript && <SerbianScriptSelect value={form.target_script} onChange={(v) => set("target_script", v)} testid="auth-serbian-script" />}
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest">{t("auth.myLevel")}</label>
                  <div className="grid grid-cols-3 gap-2 mt-1" data-testid="signup-cefr-selector">
                    {CEFR.map((c) => (
                      <button type="button" key={c.level} onClick={() => set("level", c.level)}
                        className={`text-start border-2 border-ink rounded-xl p-2 transition-all ${form.level === c.level ? "bg-mint shadow-hard-sm -translate-y-0.5" : "bg-white"}`}>
                        <div className="font-cabinet font-bold text-sm">{c.level}</div>
                        <div className="text-[10px] leading-tight text-gray-600">{t(`cefr.${c.level}`)}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={form.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)} className="mt-0.5 w-4 h-4 accent-coral" data-testid="auth-terms" required />
                  <span>{t("auth.acceptTerms")} <Link to="/terms" className="underline">{t("footer.terms")}</Link> · <Link to="/privacy" className="underline">{t("footer.privacy")}</Link></span>
                </label>
              </>
            )}

            <button type="submit" disabled={loading} className="nb-btn-primary w-full py-3" data-testid="auth-submit">
              {loading ? t("common.loading") : isSignup ? t("auth.createAccount") : t("auth.signIn")}
            </button>
          </form>

          <p className="text-sm text-center text-gray-600 mt-5">
            {isSignup ? `${t("auth.haveAccount")} ` : `${t("auth.noAccount")} `}
            <Link to={`${isSignup ? "/login" : "/signup"}${window.location.search}`} className="font-bold text-coral" data-testid="auth-switch">
              {isSignup ? t("auth.signIn") : t("auth.createAccount")}
            </Link>
          </p>
          <p className="text-[11px] text-center text-gray-400 mt-6" data-testid="auth-studio">
            GetFluentLoop · Felix Lancelot studios
          </p>
        </motion.div>
      </div>
    </div>
  );
}
